import React from "react";
import { useNavigation, useResource, useDelete } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import { Pencil, Eye, Trash2, Plus } from "lucide-react";

interface ActionButtonProps {
  recordItemId?: string | number;
  resource?: string;
  hideText?: boolean;
  size?: "default" | "sm" | "lg" | "icon";
}

export const EditButton: React.FC<ActionButtonProps> = ({ recordItemId, resource: resourceProp, hideText, size = "sm" }) => {
  const { resource } = useResource();
  const { edit } = useNavigation();
  const resourceName = resourceProp || resource?.name || "";

  return (
    <Button variant="outline" size={size} onClick={() => edit(resourceName, recordItemId ?? "")}>
      <Pencil className="h-4 w-4" />
      {!hideText && <span className="ml-1">Edit</span>}
    </Button>
  );
};

export const ShowButton: React.FC<ActionButtonProps> = ({ recordItemId, resource: resourceProp, hideText, size = "sm" }) => {
  const { resource } = useResource();
  const { show } = useNavigation();
  const resourceName = resourceProp || resource?.name || "";

  return (
    <Button variant="outline" size={size} onClick={() => show(resourceName, recordItemId ?? "")}>
      <Eye className="h-4 w-4" />
      {!hideText && <span className="ml-1">Show</span>}
    </Button>
  );
};

export const DeleteButton: React.FC<ActionButtonProps & { onSuccess?: () => void }> = ({
  recordItemId,
  resource: resourceProp,
  hideText,
  size = "sm",
  onSuccess,
}) => {
  const { resource } = useResource();
  const { mutate } = useDelete();
  const resourceName = resourceProp || resource?.name || "";

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      mutate(
        { resource: resourceName, id: recordItemId ?? "" },
        { onSuccess }
      );
    }
  };

  return (
    <Button variant="destructive" size={size} onClick={handleDelete}>
      <Trash2 className="h-4 w-4" />
      {!hideText && <span className="ml-1">Delete</span>}
    </Button>
  );
};

export const CreateButton: React.FC<ActionButtonProps & { label?: string }> = ({
  resource: resourceProp,
  hideText,
  size = "default",
  label,
}) => {
  const { resource } = useResource();
  const { create } = useNavigation();
  const resourceName = resourceProp || resource?.name || "";

  return (
    <Button size={size} onClick={() => create(resourceName)}>
      <Plus className="h-4 w-4" />
      {!hideText && <span className="ml-1">{label || "Create"}</span>}
    </Button>
  );
};
